import { Connection } from '@solana/web3.js';

/**
 * Transaction Parser for Prediction Market Positions
 * 
 * This parser is specifically for parsing prediction market smart contract events:
 * - PositionOpened events (to get position entry data)
 * - PositionClosed events (to get exit data and profit)
 * 
 * This is different from transactionParsingService.js which parses token swap transactions.
 * 
 * Key differences:
 * - Uses Anchor event discriminators from the IDL
 * - Parses structured event data, not SOL/token movements
 * - Extracts cycle_id, position data, and profit information
 */

/**
 * Helper function to wait for transaction confirmation with retry logic
 * Returns true ONLY if transaction is confirmed AND succeeded (no errors)
 */
export const waitForTransactionConfirmation = async (
  connection: Connection,
  signature: string,
  maxRetries: number = 30,
  retryInterval: number = 1000
): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const status = await connection.getSignatureStatus(signature);
      
      // ✅ Check if transaction is confirmed or finalized
      if (status.value?.confirmationStatus === 'confirmed' || 
          status.value?.confirmationStatus === 'finalized') {
        
        // ⚠️ CRITICAL: Check if transaction actually succeeded (no errors)
        if (status.value?.err) {
          console.error('❌ Transaction confirmed but FAILED on-chain:', status.value.err);
          return false;
        }
        return true;
      }
      
      // ❌ Check for early failure detection
      if (status.value?.err) {
        console.error('❌ Transaction failed before confirmation:', status.value.err);
        return false;
      }
      
    } catch (error) {
      console.warn(`Confirmation check ${i + 1} failed:`, error);
    }
    
    // Wait before next check
    await new Promise(resolve => setTimeout(resolve, retryInterval));
  }
  
  console.warn(`❌ Transaction confirmation timeout after ${maxRetries} retries`);
  return false;
};